/**
 * The account menu behind every avatar: who is signed in, their role on the active brand, and Sign out,
 * plus any page-specific actions the caller passes in. One control instead of buttons beside the avatar.
 */
import * as Popover from '@radix-ui/react-popover';
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { TenantMe } from '@studio/contracts';
import { supabase } from '@/lib/supabase';
import { initials } from './brand-switcher';

const ROLE_TITLE: Record<string, string> = { creator: 'Creator', reviewer: 'Reviewer', editor: 'Editor', owner: 'Owner' };

export interface ProfileMenuItem {
  label: string;
  onClick: () => void;
}

export function ProfileMenu({ me, items = [], avatarClass = '' }: { me: TenantMe; items?: ProfileMenuItem[]; avatarClass?: string }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const name = me.displayName || me.email;
  const role = me.active?.role ?? 'reviewer';
  const signOut = async (): Promise<void> => {
    await supabase().auth.signOut();
    await navigate({ to: '/sign-in' });
  };
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button type="button" className={`avatar avatar-btn ${avatarClass}`} aria-label="Account menu" aria-expanded={open}>{initials(name)}</button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="pop profile-pop" align="end" sideOffset={6}>
          <div className="profile-who">
            <div className="n">{name}</div>
            {me.displayName && <div className="r">{me.email}</div>}
            <div className="r">{ROLE_TITLE[role]}{me.active ? ` · ${me.active.orgName}` : ''}</div>
          </div>
          <div className="hr" />
          {items.map((item) => (
            <button key={item.label} type="button" className="profile-item" onClick={() => { setOpen(false); item.onClick(); }}>{item.label}</button>
          ))}
          <button type="button" className="profile-item" onClick={() => void signOut()}>Sign out</button>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
